import { io } from 'socket.io-client'
import Config from 'aldoo-ra/Config'
import Value from 'aldoo-ra/Value'
import { useEffect } from 'react'

// Store active socket connections
const socketConnections = new Map()

// How many consumers hold each pooled socket. The pool hands the same socket
// to everyone who asks for a namespace, so one component unmounting must not
// disconnect it under the others — it is closed when the last holder lets go
// (`releaseSocket`).
const socketHolders = new Map()

// This is a reference to the Value('user-credentials') object
// It changes based on the user login state (same pattern as API module)
let USER_CREDENTIALS = null

/**
 * This is added to the application start up and listens for
 * available user credentials. Those are then used to make
 * realtime connections to the backend (same pattern as AldooAPI)
 */
export const AldooRealtime = () => {
  const [auth] = Value("user-credentials")

  useEffect(() => {
    // Here we get the value from the auth object
    // If it's not available, we try to get the value immediately
    USER_CREDENTIALS = auth
  }, [auth])
}

/**
 * Create or get existing socket connection for a namespace
 * @param {string} namespace - The namespace to connect to (e.g., "deployment")
 * @param {Object} options - Socket connection options
 * @returns {Socket} Socket.io client instance
 */
const createSocket = (namespace, options = {}) => {
  // Get auth token from stored credentials (same pattern as API module)
  let authToken = null
  if (USER_CREDENTIALS?.auth_token) {
    authToken = USER_CREDENTIALS.auth_token
  }
  
  if (!authToken) {
    throw new Error("Authentication required for realtime connections")
  }

  const socketKey = namespace
  
  // Return the existing connection even while it is still (re)connecting.
  // socket.io reconnects the SAME instance, so every listener other
  // components bound to it survives. Recreating here would orphan those
  // listeners — e.g. a second consumer calling rt() during the initial
  // handshake used to kill the first consumer's event bindings.
  if (socketConnections.has(socketKey)) {
    const existingSocket = socketConnections.get(socketKey)
    if (existingSocket.connected || existingSocket.active) {
      socketHolders.set(socketKey, (socketHolders.get(socketKey) || 0) + 1)
      return existingSocket
    }
    // Terminal state only (manually disconnected, reconnection given up).
    existingSocket.disconnect()
    socketConnections.delete(socketKey)
  }

  // Create new socket connection
  const socketUrl = `${Config.PLATFORM_API_URL}/rt/${namespace}`
  
  const defaultOptions = {
    auth: { token: authToken },
    timeout: 10000,
    reconnection: true,
    reconnectionAttempts: Infinity, // Keep trying indefinitely
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    transports: ['websocket'], // the API cluster refuses long-polling; retries stay on WebSocket
    // the API's socket path (REALTIME_PATH) when it shares a host with the site's own /socket.io
    ...(Config.REALTIME_PATH ? { path: Config.REALTIME_PATH } : {}),
    upgrade: true, // Allow transport upgrades
    rememberUpgrade: true, // Remember successful upgrades
    pingTimeout: 60000, // Increase ping timeout to 60 seconds
    pingInterval: 25000, // Send ping every 25 seconds
    ...options
  }

  const socket = io(socketUrl, defaultOptions)

  // The token was captured at creation; a long-lived socket may outlive it
  // (credential rotation). Refresh it before every reconnection handshake so
  // the server's credential check doesn't reject the reconnect forever with
  // a token that stopped being valid.
  socket.io.on('reconnect_attempt', () => {
    const fresh = USER_CREDENTIALS?.auth_token
    if (fresh) socket.auth = { ...socket.auth, token: fresh }
  })

  // Store connection
  socketConnections.set(socketKey, socket)
  socketHolders.set(socketKey, (socketHolders.get(socketKey) || 0) + 1)

  socket.on('connect_error', (error) => {
    // Only log persistent connection errors, not temporary websocket failures
    if (error.message !== 'websocket error') {
      console.error(`Realtime: Connection error for ${namespace}:`, error.message)
    }
  })

  return socket
}

/**
 * Let go of a pooled socket.
 *
 * A consumer that is going away calls this instead of disconnecting: the
 * socket is shared, and the others still need it. It is closed when the last
 * holder releases it.
 *
 * @param {string} namespace - the namespace whose socket is being released
 */
const releaseSocket = (namespace) => {
  const holders = socketHolders.get(namespace) || 0
  if (holders > 1) {
    socketHolders.set(namespace, holders - 1)
    return false
  }
  socketHolders.delete(namespace)
  const socket = socketConnections.get(namespace)
  if (socket) {
    socket.disconnect()
    socketConnections.delete(namespace)
  }
  return true
}

/**
 * Disconnect and cleanup socket connection
 * @param {string} namespace - The namespace to disconnect from
 */
const disconnectSocket = (namespace) => {
  const socketKey = namespace
  if (socketConnections.has(socketKey)) {
    const socket = socketConnections.get(socketKey)
    socket.disconnect()
    socketConnections.delete(socketKey)
  }
}

/**
 * Disconnect all socket connections
 */
const disconnectAll = () => {
  socketConnections.forEach((socket, namespace) => {
    socket.disconnect()
  })
  socketConnections.clear()
}

/**
 * Get existing socket connection without creating new one
 * @param {string} namespace - The namespace to get
 * @returns {Socket|null} Socket instance or null if not connected
 */
const getSocket = (namespace) => {
  const socketKey = namespace
  const socket = socketConnections.get(socketKey)
  return socket && socket.connected ? socket : null
}

/**
 * Main realtime function - similar to api() function
 * Creates or returns socket connection for the specified namespace
 * @param {string} namespace - The namespace to connect to
 * @param {Object} options - Socket connection options
 * @returns {Socket} Socket.io client instance
 */
const rt = (namespace, options = {}) => {
  if (!namespace) {
    throw new Error("Namespace is required for realtime connections")
  }
  
  return createSocket(namespace, options)
}

// Export main function and utilities
export default rt
export { 
  rt,
  createSocket,
  releaseSocket,
  disconnectSocket, 
  disconnectAll,
  getSocket
}